import { useMutation } from "@apollo/client";
import FileDownload from "@mui/icons-material/FileDownload";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  CustomerEngagementTier,
  ModuleSubscriptionStatus,
  ServiceTier,
  StaffTenantSubscription,
  StaffTenantSubscriptionStats,
  StaffTenantSubscriptionsQuery,
  StaffTenantSubscriptionsQueryVariables,
  SubscriptionOverageStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { GET_STAFF_TENANT_SUBSCRIPTIONS } from "hooks/useStaffTenantSubscriptions";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { KeyMetric } from "routes/Staff/Fulfillment/WorkflowOverviewStats/KeyMetric";

type Row = StaffTenantSubscription;

const SFDC_ACCOUNT_URL = "https://encamp.lightning.force.com/lightning/r/Account";
const SFDC_REPORT_URL =
  "https://encamp.lightning.force.com/lightning/r/Report/00ONs00000Q6wvG/view";

// Stat cards route to the grid with a pre-applied omnisearch filter on one of the
// hidden status columns. URL-driven omnisearch reads from the `omnisearch` query
// param and the backend's registered `chemicalStatus` / `wasteStatus` filters
// translate that to a WHERE clause against the computed status columns.
const STAT_FILTER_HREFS = {
  chemicalOverages: `?omnisearch=${encodeURIComponent("chemicalStatus:OVER")}`,
  wasteOverages: `?omnisearch=${encodeURIComponent("wasteStatus:OVER")}`,
  chemicalWithinLimit: `?omnisearch=${encodeURIComponent("chemicalStatus:WITHIN")}`,
} as const;

function formatSyncedAt(value: string | null | undefined): string {
  if (!value) return "Never";
  return DateTime.fromISO(value).toRelative() ?? "Never";
}

function formatSyncedAtAbsolute(value: string | null | undefined): string {
  if (!value) return "—";
  return DateTime.fromISO(value).toFormat("yyyy-LL-dd HH:mm 'UTC'");
}

function engagementLabel(tier: CustomerEngagementTier | null | undefined): string {
  if (!tier) return "—";
  if (tier === CustomerEngagementTier.SmbSmall) return "SMB/Small";
  if (tier === CustomerEngagementTier.MidMarket) return "Mid-Market";
  return "Enterprise";
}

function serviceTierLabel(tier: ServiceTier | null | undefined): string {
  if (!tier) return "—";
  return prettyPrintEnumValue(tier);
}

function moduleStatusLabel(status: ModuleSubscriptionStatus | null | undefined): string {
  if (!status) return "—";
  if (status === ModuleSubscriptionStatus.Customer) return "Active";
  return "Former";
}

type OverageDirection = "over" | "within" | "unknown";

function overageDirection(
  actual: number | null | undefined,
  contracted: number | null | undefined
): OverageDirection {
  if (actual == null || contracted == null || contracted <= 0) return "unknown";
  return actual > contracted ? "over" : "within";
}

function EngagementBadge({ tier }: { tier: CustomerEngagementTier | null | undefined }) {
  if (!tier) return <Typography variant="body2">—</Typography>;
  let color: "secondary" | "encampBlue" | "grey50";
  if (tier === CustomerEngagementTier.Enterprise) {
    color = "secondary";
  } else if (tier === CustomerEngagementTier.MidMarket) {
    color = "encampBlue";
  } else {
    color = "grey50";
  }
  return <Chip size="small" color={color} label={engagementLabel(tier)} />;
}

function ServiceTierBadge({ tier }: { tier: ServiceTier | null | undefined }) {
  if (!tier) return <Typography variant="body2">—</Typography>;
  let color: "secondary" | "encampBlue" | "grey50";
  if (tier === ServiceTier.Peak) {
    color = "secondary";
  } else if (tier === ServiceTier.Climb) {
    color = "encampBlue";
  } else {
    color = "grey50";
  }
  return <Chip size="small" color={color} label={serviceTierLabel(tier)} />;
}

function ModuleStatusBadge({ status }: { status: ModuleSubscriptionStatus | null | undefined }) {
  if (!status) return <Typography variant="body2">—</Typography>;
  return (
    <Chip
      size="small"
      color={status === ModuleSubscriptionStatus.Customer ? "encampGreen" : "encampRed"}
      label={moduleStatusLabel(status)}
    />
  );
}

function StatusBadge({ status }: { status: SubscriptionOverageStatus }) {
  if (status === SubscriptionOverageStatus.None) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Chip
      size="small"
      color={status === SubscriptionOverageStatus.Over ? "encampRed" : "encampGreen"}
      label={status === SubscriptionOverageStatus.Over ? "Over" : "Within"}
    />
  );
}

function CompareCell({
  actual,
  contracted,
}: {
  actual: number | null | undefined;
  contracted: number | null | undefined;
}) {
  const theme = useTheme();
  if (actual == null) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const dir = overageDirection(actual, contracted);
  const color =
    dir === "over"
      ? theme.palette.error.main
      : dir === "within"
        ? theme.palette.success.main
        : theme.palette.text.primary;
  return (
    <Typography variant="body2" sx={{ color, fontFamily: "monospace", fontWeight: 600 }}>
      {actual.toLocaleString()}
    </Typography>
  );
}

// Facility-count columns draw from two sources: SFDC-contracted numbers synced
// from Salesforce, and Encamp-actual numbers computed from Facility state. The
// 🏕️ prefix marks columns sourced from Encamp; unmarked columns are from SFDC.
// When source === "sfdc", pass the exact field label used in the SFDC report
// ("Customers w/ App Fields"); it is appended to the tooltip so staff can trace
// a column back to its source field.
function infoHeader(label: string, tooltip: string, source: "encamp" | "sfdc", sfdcField?: string) {
  const display = source === "encamp" ? `🏕️ ${label}` : label;
  const fullTooltip = sfdcField ? `${tooltip} SFDC field: "${sfdcField}".` : tooltip;
  // Named function so React (and react/display-name) can identify the component
  // — MUI DataGrid's renderHeader expects a component-like callable here.
  const InfoHeader = () => (
    <Tooltip title={fullTooltip} placement="top">
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <span>{display}</span>
        <InfoOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
      </Stack>
    </Tooltip>
  );
  InfoHeader.displayName = `InfoHeader(${label})`;
  return InfoHeader;
}

function LinksCell({ row }: { row: Row }) {
  return (
    <Stack direction="row" spacing={0.5}>
      <Tooltip title="Open in Encamp Staff">
        <IconButton
          size="small"
          component={Link}
          to={`/staff/implementation/${row.tenantId}`}
          target="_blank"
        >
          <OpenInNew fontSize="small" />
        </IconButton>
      </Tooltip>
      {row.salesforceAccountId && (
        <Tooltip title="Open in Salesforce">
          <IconButton
            size="small"
            component="a"
            href={`${SFDC_ACCOUNT_URL}/${row.salesforceAccountId}/view`}
            target="_blank"
            rel="noopener"
          >
            <OpenInNew fontSize="small" sx={{ color: "#00A1E0" }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

const EXPORT_STAFF_TENANT_SUBSCRIPTIONS_TO_CSV = gql(`
  mutation ExportStaffTenantSubscriptionsToCsv($search: String, $sort: [SortModel!]) {
    exportStaffTenantSubscriptionsToCsv(search: $search, sort: $sort) {
      success
      errorMsg
      downloadUrl
    }
  }
`);

function CsvExportButton({ search }: { search: string | undefined }) {
  const alert = useAlerts();
  const [mutate, { data, loading }] = useMutation(EXPORT_STAFF_TENANT_SUBSCRIPTIONS_TO_CSV);
  const result = data?.exportStaffTenantSubscriptionsToCsv;

  useEffect(() => {
    if (result?.success) {
      alert.success("Successfully generated the export");
    } else if (result?.errorMsg) {
      alert.error(`There was a problem generating the export: ${result.errorMsg}`);
    }
  }, [alert, result?.errorMsg, result?.success]);

  useEffect(() => {
    if (result?.downloadUrl) {
      window.open(result.downloadUrl, "_blank");
    }
  }, [result?.downloadUrl]);

  return (
    <Tooltip title="Download all rows matching the current filter as CSV." placement="top">
      <span>
        <Button
          size="medium"
          loading={loading}
          sx={{ minWidth: 0 }}
          onClick={() => mutate({ variables: { search, sort: [] } })}
        >
          <FileDownload />
        </Button>
      </span>
    </Tooltip>
  );
}

export const SubscriptionOverview = () => {
  useBreadcrumb({ label: "Subscription Overview" });

  const [stats, setStats] = useState<StaffTenantSubscriptionStats | undefined>(undefined);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null | undefined>(undefined);
  const [statsLoading, setStatsLoading] = useState(true);
  // Mirror the omnisearch string into local state so the CSV export mutation
  // can pass it to the backend (which then runs the same filter + no limit).
  const [currentSearch, setCurrentSearch] = useState<string>("");

  const handleQueryStateChange = useCallback(
    ({ data, loading }: { data: StaffTenantSubscriptionsQuery | undefined; loading: boolean }) => {
      setStats(data?.staffTenantSubscriptions.stats);
      setLastSyncedAt(data?.staffTenantSubscriptions.lastSyncedAt);
      setStatsLoading(loading);
    },
    []
  );

  const commandButtons = useMemo(
    () => [<CsvExportButton key="csv" search={currentSearch} />],
    [currentSearch]
  );

  return (
    <Stack py={2} spacing={1.5}>
      {/* Compact page meta — title is already shown in the breadcrumb */}
      <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          Contracted vs actual facility counts from{" "}
          <a href={SFDC_REPORT_URL} target="_blank" rel="noopener noreferrer">
            SFDC Report 00ONs00000Q6wvG
          </a>
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={`Last synced: ${formatSyncedAtAbsolute(lastSyncedAt)}`} placement="top">
          <Typography variant="caption" color="text.secondary">
            Data as of {formatSyncedAt(lastSyncedAt)}
          </Typography>
        </Tooltip>
      </Stack>

      {/* Stats cards */}
      <Grid container spacing={1.5}>
        <Grid item xs={6} md>
          <KeyMetric
            name="Accounts w/ Subscription"
            value={stats?.totalTenants}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} md>
          <KeyMetric
            name="Contracted Platform Facilities"
            value={stats?.totalContractedPlatformFacilities}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} md>
          <KeyMetric
            name="Chemical Overages"
            value={stats?.chemicalOverages}
            loading={statsLoading}
            href={STAT_FILTER_HREFS.chemicalOverages}
          />
        </Grid>
        <Grid item xs={6} md>
          <KeyMetric
            name="Waste Overages"
            value={stats?.wasteOverages}
            loading={statsLoading}
            href={STAT_FILTER_HREFS.wasteOverages}
          />
        </Grid>
        <Grid item xs={6} md>
          <KeyMetric
            name="Chemical Within Limit"
            value={stats?.chemicalWithinLimit}
            loading={statsLoading}
            href={STAT_FILTER_HREFS.chemicalWithinLimit}
          />
        </Grid>
      </Grid>

      {/* Data grid */}
      <OmnisearchDataGrid<
        StaffTenantSubscriptionsQuery,
        StaffTenantSubscriptionsQueryVariables,
        Row
      >
        persistenceKey="staffTenantSubscriptions"
        isURLDriven
        onQueryStateChange={handleQueryStateChange}
        onSearchChanged={setCurrentSearch}
        getRowId={(row) => row.tenantId}
        columns={[
          {
            field: "tenantName",
            headerName: "Account",
            flex: 1.2,
            minWidth: 220,
            renderCell({ row }) {
              return (
                <Stack spacing={0.25} py={0.5}>
                  <Typography variant="body2" fontWeight={500}>
                    {row.tenantName}
                    {row.isPartner && (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 0.5 }}
                      >
                        (<em>Partner</em>)
                      </Typography>
                    )}
                  </Typography>
                  {(row.isDemo || row.isDisabled) && (
                    <Stack direction="row" spacing={0.5}>
                      {row.isDemo && <Chip size="small" label="Demo" color="grey50" />}
                      {row.isDisabled && <Chip size="small" label="Disabled" color="encampRed" />}
                    </Stack>
                  )}
                </Stack>
              );
            },
          },
          {
            field: "customerEngagementTier",
            headerName: "Engagement",
            renderHeader: infoHeader(
              "Engagement",
              "Customer engagement tier.",
              "sfdc",
              "Customer Engagement Tier"
            ),
            flex: 0.6,
            minWidth: 120,
            filterKeyType: "enum",
            enumValues: Object.values(CustomerEngagementTier),
            renderCell({ row }) {
              return <EngagementBadge tier={row.customerEngagementTier} />;
            },
          },
          {
            field: "chemicalServiceTier",
            headerName: "Chem Tier",
            renderHeader: infoHeader(
              "Chem Tier",
              "Chemical service tier.",
              "sfdc",
              "Chemical Service Tier"
            ),
            flex: 0.5,
            minWidth: 100,
            filterKeyType: "enum",
            enumValues: Object.values(ServiceTier),
            renderCell({ row }) {
              return <ServiceTierBadge tier={row.chemicalServiceTier} />;
            },
          },
          {
            field: "wasteServiceTier",
            headerName: "Waste Tier",
            renderHeader: infoHeader(
              "Waste Tier",
              "Waste service tier.",
              "sfdc",
              "Waste Service Tier"
            ),
            flex: 0.5,
            minWidth: 100,
            filterKeyType: "enum",
            enumValues: Object.values(ServiceTier),
            renderCell({ row }) {
              return <ServiceTierBadge tier={row.wasteServiceTier} />;
            },
          },
          {
            field: "contractedIntelligencePlatformFacilities",
            headerName: "Platform",
            renderHeader: infoHeader(
              "Platform",
              "Contracted count of facilities on the Encamp platform.",
              "sfdc",
              "# Intelligence Platform Facilities"
            ),
            flex: 0.5,
            minWidth: 95,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "actualPlatformFacilities",
            headerName: "Platform Actual",
            renderHeader: infoHeader(
              "Platform Actual",
              "Active facilities on the Encamp platform right now (status ≠ CLOSED). From Encamp.",
              "encamp"
            ),
            flex: 0.6,
            minWidth: 130,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "contractedInsightsFacilities",
            headerName: "Insights Sub",
            renderHeader: infoHeader(
              "Insights Sub",
              "Contracted count of facilities on the Insights subscription.",
              "sfdc",
              "# Insights Subscription Facilities"
            ),
            flex: 0.5,
            minWidth: 110,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "contractedChemicalSubscriptionFacilities",
            headerName: "Chem Sub",
            renderHeader: infoHeader(
              "Chem Sub",
              "Contracted count of facilities with a chemical subscription.",
              "sfdc",
              "# Chemical Subscription Facilities"
            ),
            flex: 0.5,
            minWidth: 95,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "contractedChemicalReportingFacilities",
            headerName: "Chem Rpt",
            renderHeader: infoHeader(
              "Chem Rpt",
              "Contracted count of facilities included in chemical reporting.",
              "sfdc",
              "# Chemical Reporting Facilities"
            ),
            flex: 0.5,
            minWidth: 95,
            align: "right",
            headerAlign: "right",
          },
          {
            field: "actualChemicalFacilities",
            headerName: "Chem Actual",
            renderHeader: infoHeader(
              "Chem Actual",
              "Active facilities with an effective chemical subscription: per-facility override ENABLED, or INHERIT combined with the org-level chemical module flag. Compared against Chem Sub. From Encamp.",
              "encamp"
            ),
            flex: 0.6,
            minWidth: 115,
            align: "right",
            headerAlign: "right",
            sortable: true,
            renderCell({ row }) {
              return (
                <CompareCell
                  actual={row.actualChemicalFacilities}
                  contracted={row.contractedChemicalSubscriptionFacilities}
                />
              );
            },
          },
          {
            field: "contractedWasteSubscriptionFacilities",
            headerName: "Waste Sub",
            renderHeader: infoHeader(
              "Waste Sub",
              "Contracted count of facilities with a waste subscription.",
              "sfdc",
              "# Waste Subscription Facilities"
            ),
            flex: 0.5,
            minWidth: 100,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "contractedWasteReportingBiennialFacilities",
            headerName: "Waste Biennial",
            renderHeader: infoHeader(
              "Waste Biennial",
              "Contracted count of facilities included in biennial waste reporting.",
              "sfdc",
              "# Waste Reporting Facilities - Biennial"
            ),
            flex: 0.5,
            minWidth: 125,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "contractedWasteReportingNonBiennialFacilities",
            headerName: "Waste Non-Biennial",
            renderHeader: infoHeader(
              "Waste Non-Biennial",
              "Contracted count of facilities included in non-biennial waste reporting.",
              "sfdc",
              "# Waste Reporting Fac - non-Biennial"
            ),
            flex: 0.5,
            minWidth: 150,
            align: "right",
            headerAlign: "right",
            sortable: true,
          },
          {
            field: "actualWasteFacilities",
            headerName: "Waste Actual",
            renderHeader: infoHeader(
              "Waste Actual",
              "Active facilities with an effective waste subscription: per-facility override ENABLED, or INHERIT combined with the org-level waste module flag. Compared against Waste Sub. From Encamp.",
              "encamp"
            ),
            flex: 0.6,
            minWidth: 115,
            align: "right",
            headerAlign: "right",
            sortable: true,
            renderCell({ row }) {
              return (
                <CompareCell
                  actual={row.actualWasteFacilities}
                  contracted={row.contractedWasteSubscriptionFacilities}
                />
              );
            },
          },
          {
            field: "epcraSubscriptionStatus",
            headerName: "EPCRA",
            renderHeader: infoHeader(
              "EPCRA",
              "EPCRA (Tier II) module subscription status.",
              "sfdc",
              "EPCRA Subscription Status"
            ),
            flex: 0.4,
            minWidth: 95,
            sortable: false,
            renderCell({ row }) {
              return <ModuleStatusBadge status={row.epcraSubscriptionStatus} />;
            },
          },
          {
            field: "rcraSubscriptionStatus",
            headerName: "RCRA",
            renderHeader: infoHeader(
              "RCRA",
              "RCRA (waste) module subscription status.",
              "sfdc",
              "RCRA Subscription Status"
            ),
            flex: 0.4,
            minWidth: 95,
            sortable: false,
            renderCell({ row }) {
              return <ModuleStatusBadge status={row.rcraSubscriptionStatus} />;
            },
          },
          {
            field: "insightsSubscriptionStatus",
            headerName: "Insights",
            renderHeader: infoHeader(
              "Insights",
              "Insights module subscription status.",
              "sfdc",
              "Insights Subscription Status"
            ),
            flex: 0.5,
            minWidth: 110,
            sortable: false,
            renderCell({ row }) {
              return <ModuleStatusBadge status={row.insightsSubscriptionStatus} />;
            },
          },
          {
            field: "calendarSubscriptionStatus",
            headerName: "Calendar",
            renderHeader: infoHeader(
              "Calendar",
              "Calendar module subscription status.",
              "sfdc",
              "Calendar Subscription Status"
            ),
            flex: 0.5,
            minWidth: 110,
            sortable: false,
            renderCell({ row }) {
              return <ModuleStatusBadge status={row.calendarSubscriptionStatus} />;
            },
          },
          {
            field: "chemicalsDataPipeline",
            headerName: "Chem Pipeline",
            renderHeader: infoHeader(
              "Chem Pipeline",
              "Whether the tenant has the chemicals data pipeline enabled.",
              "sfdc",
              "Chemicals Data Pipeline?"
            ),
            flex: 0.4,
            minWidth: 115,
            sortable: false,
            renderCell({ row }) {
              return row.chemicalsDataPipeline ? (
                <Chip size="small" label="Yes" color="encampGreen" />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              );
            },
          },
          {
            field: "links",
            headerName: "",
            flex: 0.3,
            minWidth: 75,
            sortable: false,
            renderCell({ row }) {
              return <LinksCell row={row} />;
            },
          },
          // Hidden-by-default columns that power the overage filters. Filter
          // chips and the stat-card deep links route through these fields.
          {
            field: "chemicalStatus",
            headerName: "Chem Status",
            filterKeyType: "enum",
            enumValues: Object.values(SubscriptionOverageStatus),
            flex: 0.5,
            minWidth: 115,
            sortable: false,
            renderCell({ row }) {
              return <StatusBadge status={row.chemicalStatus} />;
            },
          },
          {
            field: "wasteStatus",
            headerName: "Waste Status",
            filterKeyType: "enum",
            enumValues: Object.values(SubscriptionOverageStatus),
            flex: 0.5,
            minWidth: 115,
            sortable: false,
            renderCell({ row }) {
              return <StatusBadge status={row.wasteStatus} />;
            },
          },
        ]}
        initialColumnVisibilityModel={{
          contractedChemicalReportingFacilities: false,
          chemicalStatus: false,
          wasteStatus: false,
        }}
        dataQuery={GET_STAFF_TENANT_SUBSCRIPTIONS}
        getItems={(data) => data.staffTenantSubscriptions.items}
        getCount={(data) => data.staffTenantSubscriptions.count}
        excludeFilterColumns={["links", "contractedChemicalReportingFacilities"]}
        commandButtons={commandButtons}
      />
    </Stack>
  );
};
